import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';

/** Max avatar size: 512KB. Mirrors the server's multer limit. */
const MAX_AVATAR_BYTES = 512 * 1024;

/**
 * Legacy localStorage key, kept only to migrate pictures uploaded before
 * avatars were stored server-side.
 */
function legacyStorageKey(username: string): string {
  return `vertex-hub-avatar-${username}`;
}

/**
 * Shared picture state.
 *
 * Module-level rather than per-hook because TopNav and ProfileModal both call
 * this hook. With independent `useState` they disagreed after an upload: the
 * modal showed the new picture while the nav kept the old one until something
 * remounted.
 *
 * `undefined` means "not fetched yet", which is distinct from `null` meaning
 * "fetched, and the user has no picture".
 */
let cached: string | null | undefined;
let inFlight: Promise<void> | null = null;
const subscribers = new Set<(value: string | null) => void>();

function publish(value: string | null): void {
  cached = value;
  for (const fn of subscribers) fn(value);
}

/** Convert a stored data URL back into a File so it can be uploaded as-is. */
async function dataUrlToFile(dataUrl: string): Promise<File | null> {
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    if (!blob.type.startsWith('image/') || blob.size > MAX_AVATAR_BYTES) return null;
    const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
    return new File([blob], `avatar.${ext}`, { type: blob.type });
  } catch {
    return null;
  }
}

async function postAvatar(token: string, file: File): Promise<string> {
  const body = new FormData();
  body.append('avatar', file);
  const res = await fetch('/api/users/me/avatar', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(detail?.error ?? 'Upload failed');
  }
  const data = (await res.json()) as { pictureUrl?: string };
  if (!data.pictureUrl) throw new Error('Upload returned no URL');
  return data.pictureUrl;
}

/**
 * Load the current picture once per page load, migrating a legacy localStorage
 * avatar on the way if the server has none. Best-effort: a failed migration
 * leaves the local copy in place so the next load can retry.
 */
async function loadOnce(token: string, username: string): Promise<void> {
  const res = await fetch('/api/users/me/avatar', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Avatar lookup failed: ${res.status}`);
  const data = (await res.json()) as { pictureUrl?: string | null };

  if (data.pictureUrl) {
    try { localStorage.removeItem(legacyStorageKey(username)); } catch { /* ignore */ }
    publish(data.pictureUrl);
    return;
  }

  const legacy = (() => {
    try { return localStorage.getItem(legacyStorageKey(username)); } catch { return null; }
  })();
  if (!legacy?.startsWith('data:image/')) { publish(null); return; }

  const file = await dataUrlToFile(legacy);
  if (!file) { publish(null); return; }
  const url = await postAvatar(token, file);
  try { localStorage.removeItem(legacyStorageKey(username)); } catch { /* ignore */ }
  publish(url);
}

interface UseProfilePictureReturn {
  pictureUrl: string | null;
  uploadPicture: (file: File) => Promise<void>;
  removePicture: () => Promise<void>;
}

/**
 * Profile picture backed by the `picture` user attribute in Keycloak.
 *
 * Stored server-side rather than in localStorage so it survives a new browser
 * or a cleared cache, and so every other app in the realm receives it as the
 * standard OIDC `picture` claim.
 */
export function useProfilePicture(username: string): UseProfilePictureReturn {
  const { token } = useAuth();
  const [pictureUrl, setPictureUrl] = useState<string | null>(cached ?? null);

  useEffect(() => {
    subscribers.add(setPictureUrl);
    if (cached === undefined && !inFlight && token) {
      inFlight = loadOnce(token, username)
        .catch(() => { publish(null); })
        .finally(() => { inFlight = null; });
    }
    return () => { subscribers.delete(setPictureUrl); };
  }, [token, username]);

  const uploadPicture = useCallback(async (file: File): Promise<void> => {
    if (file.size > MAX_AVATAR_BYTES) throw new Error('Image must be under 512KB');
    publish(await postAvatar(token, file));
  }, [token]);

  const removePicture = useCallback(async (): Promise<void> => {
    const res = await fetch('/api/users/me/avatar', {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to remove picture');
    try { localStorage.removeItem(legacyStorageKey(username)); } catch { /* ignore */ }
    publish(null);
  }, [token, username]);

  return { pictureUrl, uploadPicture, removePicture };
}
